import { useState } from 'react'

import { Box, Button, List, Text } from '@factorialco/deprecated-design-system'
import {
  PermissionsContextEnum,
  PermissionsGetPermissionsDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { contextLabel } from 'modules/permissions/lib/contexts'
import permissions from 'modules/permissions/store/collections/permissions'

interface PermissionChange {
  id: string
  verb: string
  verbLabel: string
  context: PermissionsContextEnum
  resource: string | null
  /**
   * The new `authorized` state to be applied.
   */
  authorized: boolean
  permissionsGroupId: string
}

interface Props {
  onClose: () => void
  changes: PermissionChange[]
  permissionGroupId: string
}

const ConfirmationModal = ({ onClose, changes, permissionGroupId }: Props) => {
  const [saving, setSaving] = useState(false)
  const { company } = useCurrentSession()

  const permissionsQuery = useQuery(PermissionsGetPermissionsDocument, {
    variables: {
      permissionsGroupId: permissionGroupId,
      companyId: company.id.toString(),
    },
  })

  async function onSubmit() {
    setSaving(true)
    app.setProgress({
      message: i18n.t('common.saving'),
    })

    const { success } = await asyncAction(async () => {
      await permissions.bulkUpsert(
        changes.map((change) => ({
          verb: change.verb,
          context: change.context,
          resource: change.resource,
          authorized: change.authorized,
          permissions_group_id: permissionGroupId,
        }))
      )
      await permissionsQuery.refetch()
    })

    setSaving(false)
    app.setProgress(null)

    if (success) {
      onClose()
    }
  }

  return (
    <Modal
      locked
      title={i18n.t('common.save_changes')}
      description={i18n.t('settings.permissions.group.confirm_changes')}
      illustration="alert"
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" disabled={saving} onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="negative" disabled={saving} submit>
            {i18n.t('common.save_changes')}
          </Button>
        </>
      }
    >
      <Box width="full">
        <List>
          {changes.map((change) => (
            <List.Item key={change.id}>
              <Box
                width="full"
                flexDirection="row"
                justifyContent="spaceBetween"
                alignItems="flexEnd"
              >
                <Box flexDirection="column">
                  <List.Title>{change.verbLabel}</List.Title>
                  <List.Description>{contextLabel(change.context)}</List.Description>
                </Box>
                <Box alignItems="center" justifyContent="flexEnd">
                  <Text
                    color={change.authorized ? 'viridian1000' : 'radicalRed1500'}
                    size="300"
                    weight="semibold"
                  >
                    {change.authorized
                      ? i18n.t('common.toggled.enabled')
                      : i18n.t('common.toggled.disabled')}
                  </Text>
                </Box>
              </Box>
            </List.Item>
          ))}
        </List>
      </Box>
    </Modal>
  )
}

export default observer(ConfirmationModal)
